// src/components/profile/VAProfileView.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  FiUser, FiMapPin, FiClock, FiDollarSign, FiStar, FiMail,
  FiPhone, FiGlobe, FiCalendar, FiMessageSquare, FiEdit, FiShare
} from 'react-icons/fi';
import { Sparkles, Clock, CheckCircle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { jobService } from '../../services/jobService';
import JobHistory from './JobHistory';

const VAProfileView = () => {
  const { vaId } = useParams();
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [profile, setProfile] = useState(null);
  const [jobHistory, setJobHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showContactModal, setShowContactModal] = useState(false);

  // Check if this is the VA's own profile
  const isOwnProfile = userProfile?.uid === vaId || (!vaId && userProfile?.role === 'va');

  useEffect(() => {
    loadProfile();
  }, [vaId, userProfile]);

  const loadProfile = async () => {
    try {
      setLoading(true);
      
      let profileData;
      if (isOwnProfile) {
        // Load current user's profile
        profileData = await jobService.getVAProfile(userProfile.uid);
      } else {
        // Load specific VA's profile
        profileData = await jobService.getVAProfileById(vaId);
      }
      
      if (profileData) {
        setProfile(profileData);
        
        // Load job history with ratings
        const history = await jobService.getVAJobHistory(profileData.id);
        setJobHistory(history);
      } else {
        setError('Profile not found');
      }
    } catch (error) {
      console.error('Error loading VA profile:', error);
      setError('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: profile?.currency || 'ZAR'
    }).format(amount);
  };

  const getAvailabilityColor = (availability) => {
    switch (availability) {
      case 'available': return 'text-green-600 bg-green-100';
      case 'busy': return 'text-yellow-600 bg-yellow-100';
      case 'unavailable': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const calculateAverageRating = () => {
    if (jobHistory.length === 0) return 0;
    const ratedJobs = jobHistory.filter(job => job.rating > 0);
    if (ratedJobs.length === 0) return 0;
    
    const sum = ratedJobs.reduce((acc, job) => acc + job.rating, 0);
    return (sum / ratedJobs.length).toFixed(1);
  };

  const getResponseTime = () => {
    // This would be calculated from actual data
    return '< 2 hours';
  };

  const handleContactVA = () => {
    if (userProfile?.role === 'client') {
      setShowContactModal(true);
    } else {
      navigate('/auth/login');
    }
  };

  const handleHireVA = () => {
    if (userProfile?.role === 'client') {
      navigate('/jobs/post', { state: { preferredVA: profile.id } });
    } else {
      navigate('/auth/login');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-2xl mx-auto py-12 text-center">
        <div className="text-red-600 mb-4">{error}</div>
        <button
          onClick={() => navigate(-1)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Go Back
        </button>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="max-w-2xl mx-auto py-12 text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Profile Not Found</h2>
        <p className="text-gray-600 mb-6">The profile you're looking for doesn't exist or is private.</p>
        <button
          onClick={() => navigate('/jobs/browse-vas')}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Browse Other VAs
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto py-8 space-y-8">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            {isOwnProfile ? 'My Profile' : `${profile.displayName}'s Profile`}
          </h1>
          <p className="text-gray-600 mt-2">
            {isOwnProfile ? 'How clients see your profile' : 'Virtual Assistant Profile'}
          </p>
        </div>
        
        {isOwnProfile && (
          <div className="flex items-center space-x-3">
            <button
              onClick={() => navigate('/profile/va-setup')}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center"
            >
              <FiEdit className="mr-2" size={16} />
              Edit Profile
            </button>
            <button className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center">
              <FiShare className="mr-2" size={16} />
              Share
            </button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-8">
          {/* Profile Header */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-start space-x-6">
              <div className="flex-shrink-0">
                {profile.profilePhoto ? (
                  <img
                    src={profile.profilePhoto}
                    alt={profile.displayName}
                    className="w-24 h-24 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-24 h-24 bg-gray-200 rounded-full flex items-center justify-center">
                    <FiUser className="text-gray-400" size={32} />
                  </div>
                )}
              </div>
              
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-2">
                  <h2 className="text-2xl font-bold text-gray-900">{profile.displayName}</h2>
                  {profile.isVerified && (
                    <CheckCircle className="text-blue-600" size={20} />
                  )}
                </div>
                
                <p className="text-lg text-gray-600 mb-3">{profile.title}</p>
                
                <div className="flex items-center space-x-6 text-sm text-gray-600 mb-4">
                  <div className="flex items-center">
                    <FiMapPin size={14} className="mr-1" />
                    {profile.location}
                  </div>
                  <div className="flex items-center">
                    <FiClock size={14} className="mr-1" />
                    {profile.timezone}
                  </div>
                  <div className="flex items-center">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getAvailabilityColor(profile.availability)}`}>
                      {profile.availability}
                    </span>
                  </div>
                </div>
                
                <div className="flex items-center space-x-6">
                  <div className="flex items-center">
                    <FiStar className="text-yellow-400 mr-1" size={16} />
                    <span className="font-medium">{calculateAverageRating()}</span>
                    <span className="text-gray-600 ml-1">({jobHistory.filter(j => j.rating > 0).length} reviews)</span>
                  </div>
                  <div className="text-sm text-gray-600">
                    Response time: {getResponseTime()}
                  </div>
                  <div className="text-sm text-gray-600">
                    {profile.completedJobs || jobHistory.filter(j => j.status === 'completed').length} jobs completed
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* About */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">About</h3>
            <p className="text-gray-700 leading-relaxed">{profile.bio}</p>
          </div>

          {/* Skills */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Skills</h3>
            <div className="flex flex-wrap gap-2">
              {profile.skills?.map((skill, index) => (
                <span
                  key={index}
                  className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium"
                >
                  {skill}
                </span>
              ))}
            </div>
          </div>

          {/* Services & Rates */}
          {profile.services && profile.services.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Services & Rates</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {profile.services.map((service, index) => (
                  <div key={index} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                    <span className="font-medium text-gray-900">{service.name}</span>
                    <span className="text-lg font-semibold text-blue-600">
                      {formatCurrency(service.rate)}/{service.unit}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Languages */}
          {profile.languages && profile.languages.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Languages</h3>
              <div className="flex flex-wrap gap-2">
                {profile.languages.map((language, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium"
                  >
                    {language}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Job History & Reviews */}
          <JobHistory 
            jobHistory={jobHistory}
            isOwnProfile={isOwnProfile}
          />
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Quick Stats */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-4">Quick Stats</h4>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Base Rate</span>
                <span className="font-semibold text-gray-900">
                  {formatCurrency(profile.hourlyRate)}/hour
                </span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Availability</span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getAvailabilityColor(profile.availability)}`}>
                  {profile.availability}
                </span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Response Time</span>
                <span className="font-semibold text-gray-900">{getResponseTime()}</span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Jobs Completed</span>
                <span className="font-semibold text-gray-900">
                  {jobHistory.filter(j => j.status === 'completed').length}
                </span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Member Since</span>
                <span className="font-semibold text-gray-900">
                  {new Date(profile.createdAt).toLocaleDateString('en-US', { 
                    month: 'short', 
                    year: 'numeric' 
                  })}
                </span>
              </div>
            </div>
          </div>

          {/* Contact Actions */}
          {!isOwnProfile && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-4">Get in Touch</h4>
              
              <div className="space-y-3">
                <button
                  onClick={handleHireVA}
                  className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                >
                  Hire {profile.displayName}
                </button>
                
                <button
                  onClick={handleContactVA}
                  className="w-full px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center"
                >
                  <FiMessageSquare className="mr-2" size={16} />
                  Send Message
                </button>
              </div>
              
              {profile.instantBooking && (
                <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center text-green-700">
                    <Sparkles className="mr-2" size={16} />
                    <span className="text-sm font-medium">Instant Booking Available</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Profile Verification */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-4">Verification</h4>
            
            <div className="space-y-3">
              <div className="flex items-center">
                <CheckCircle className="text-green-500 mr-3" size={16} />
                <span className="text-sm text-gray-700">Identity Verified</span>
              </div>
              
              <div className="flex items-center">
                <CheckCircle className="text-green-500 mr-3" size={16} />
                <span className="text-sm text-gray-700">Email Verified</span>
              </div>
              
              {profile.phone && (
                <div className="flex items-center">
                  <CheckCircle className="text-green-500 mr-3" size={16} />
                  <span className="text-sm text-gray-700">Phone Verified</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Contact Modal */}
      {showContactModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold">Contact {profile.displayName}</h3>
            </div>
            
            <div className="p-6">
              <p className="text-gray-600 mb-4">
                Send a message to {profile.displayName} to discuss your project requirements.
              </p>
              
              <textarea
                placeholder="Hi, I'm interested in your services..."
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => setShowContactModal(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                Send Message
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VAProfileView;